import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

export const SUPPORTED_LANGUAGES = ['en', 'fr', 'es', 'ar'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_STORAGE_KEY = 'app_language';

// Languages whose script reads right-to-left. Used to flip the document
// direction so the UI mirrors correctly.
const RTL_LANGUAGES = new Set<string>(['ar']);

// Keep the <html> lang/dir attributes in sync with the active language so
// RTL scripts (e.g. Arabic) render correctly.
const applyDocumentDirection = (language: string) => {
  const base = (language || 'en').split('-')[0];
  document.documentElement.lang = base;
  document.documentElement.dir = RTL_LANGUAGES.has(base) ? 'rtl' : 'ltr';
};

i18n
  // Translation dictionaries are served as static JSON from /locales instead
  // of being bundled into the app, so they don't inflate the JS bundle and
  // are fetched (and cached) on demand only for the active language.
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    backend: {
      loadPath: `${import.meta.env.BASE_URL}locales/{{lng}}/translation.json`,
    },
    fallbackLng: false,
    keySeparator: false,
    nsSeparator: false,
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    // Render synchronously with the key (English source) as fallback while
    // the target-language JSON is being fetched, instead of suspending and
    // showing a blank component. Also makes tests deterministic — jsdom has
    // no HTTP, so without this useTranslation would suspend indefinitely.
    react: {
      useSuspense: false,
    },
  });

applyDocumentDirection(i18n.language);
i18n.on('languageChanged', applyDocumentDirection);

export default i18n;
